import React, { useState } from 'react';
import { FormProvider, RHFDateField, RHFDateTimeField, RHFTextField, RHFCheckbox } from '../../../common/hook-form';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { apiConstants, postGeneric } from '../../../common/constants';
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import { convertDate, convertDateTimeISO } from '../../../helpers/UtilityHelper';
import { Button, Grid } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';


const LabelDownloadBulkB2B = () => {
    const merchantId = useSelector((state) => state.user.userInfo.merchant.id);
    const [isDownloadingFile, setIsDownloadingFile] = useState(false)

    const method = useForm({
        resolver: yupResolver(Yup.object().shape({
            date: Yup.date()
                .typeError('Date is required')
                .required('Date is required'),
            fromTime: Yup.date()
                .nullable()
                .typeError('Invalid time')
                .when('toTime', {
                    is: (to) => !!to,
                    then: (schema) => schema.required('From time is required'),
                }),
            toTime: Yup.date()
                .nullable()
                .typeError('Invalid time')
                .when('fromTime', {
                    is: (from) => !!from,
                    then: (schema) =>
                        schema
                            .required('To time is required')
                            .test('after-from', 'To time must be after From time', function (toTime) {
                                const fromTime = this.parent.fromTime;
                                if (!fromTime || !toTime) return true;
                                return new Date(toTime).getTime() > new Date(fromTime).getTime();
                            }),
                }),
            pincode: Yup.string()
                .test('valid-pincode', 'Invalid pincode', value => {
                    if (!value) {
                        return false; // Empty value
                    }

                    const regex = /^\d{6}$/; // 6-digit number
                    return regex.test(value) || value.length !== 6;
                })
                .required("Pincode is a mandatory field")
                .test('length', 'Please enter a 6 digit pincode', value => {
                    return !value || value.length === 6;
                })
                .test('valid', 'Please enter a valid pincode', value => {
                    if (!value || value.length !== 6) {
                        return true; // Skip validation for non-6 digit values
                    }

                    const regex = /^\d{6}$/; // 6-digit number
                    return regex.test(value);
                }),
        }, [['fromTime', 'toTime']])),
        defaultValues: {
            pincode: '',
            date: '',
            fromTime: null,
            toTime: null,
            cancelled_orders: false,
            manifested_orders: false
        }
    });
    const { handleSubmit, reset, watch } = method;
    const fromTimeWatch = watch('fromTime');

    const buildSlotDateTime = (date, time) => {
        const d = new Date(date);
        const t = new Date(time);
        d.setHours(t.getHours(), t.getMinutes(), 0, 0);
        return convertDateTimeISO(d);
    };

    const onSubmit = async (data) => {
        setIsDownloadingFile(true)
        try {
            const params = new URLSearchParams();
            if (data.fromTime && data.toTime) {
                params.append('fromDateTime', buildSlotDateTime(data.date, data.fromTime));
                params.append('toDateTime', buildSlotDateTime(data.date, data.toTime));
            } else {
                params.append('date', convertDate(data.date));
            }
            params.append('pincode', data.pincode);
            params.append('merchantId', merchantId);
            params.append('shipmentType', 'B2B');
            params.append('includeCancelOrders', data.cancelled_orders);
            params.append('includeManifested', data.manifested_orders);

            await postGeneric(`${apiConstants.GENERATE_BULK_LABELS}?${params.toString()}`).then(response => {
                window.open(response.data?.mergedPdf, true)
            }).catch(err => {
                console.error(err);
                const message = err?.response?.data?.message || err.message
                toast(message || "Error downloading file", { type: 'error', theme: "colored" })
                setIsDownloadingFile(false);
            });
        } catch (err) {
            const message = err?.response?.data?.message || err.message
            toast(message || "Error downloading file", { type: 'error', theme: "colored" })
        }

        setIsDownloadingFile(false)
    }

    return (
        <>
            <FormProvider methods={method} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <RHFDateField label="Date" name="date" size="small" />
                    </Grid>
                    <Grid item xs={3}>
                        <RHFDateTimeField
                            label="From Time (optional)"
                            name="fromTime"
                            mode="time"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <RHFDateTimeField
                            label="To Time (optional)"
                            name="toTime"
                            mode="time"
                            isEndTime
                            startTime={fromTimeWatch}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <RHFTextField
                            label="Pincode *"
                            name="pincode"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3} sx={{ mt: 1 }}>
                        <RHFCheckbox
                            name="cancelled_orders"
                            mandatory="false"
                            label={(
                                <>
                                    Include Cancelled Orders
                                </>
                            )}
                        />
                    </Grid>
                    <Grid item xs={3} sx={{ mt: 1 }}>
                        <RHFCheckbox
                            name="manifested_orders"
                            mandatory="false"
                            label={(
                                <>
                                    Include Manifested Orders
                                </>
                            )}
                        />
                    </Grid>
                    <Grid item xs={10} display={'flex'} alignItems={'center'}>
                        <LoadingButton
                            type="submit"
                            loading={isDownloadingFile}
                            variant="contained"
                            sx={primaryBtnSx}
                            size="small">
                            Download
                        </LoadingButton>
                        <Button
                            sx={{ ...secondaryBtnSx, ml: 1 }}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                reset()
                            }}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </FormProvider>
        </>
    )
}
export default LabelDownloadBulkB2B;
